<template>
  <component
    :is="contextComponent.component"
    v-if="contextComponent"
    v-bind="contextComponent.props"
    @apply="applyContext"
  >
    <template #default="{ toggleMenu }">
      <icon-base
        :icon="IconCog"
        class="cursor-pointer"
        :class="contextComponent.iconClass"
        view-box="-2 -2 14 14"
        :data-cy="contextComponent.dataCy"
        @click.stop="toggleMenu"
      />
    </template>
  </component>
</template>

<script setup lang="ts">
import { computed } from "vue";

import ApplyDateScope from "@/components/customAttributes/formula/ApplyDateScope.vue";
import ApplyScoreModel from "@/components/customAttributes/formula/ApplyScoreModel.vue";
import ApplyValuesDateScope from "@/components/customAttributes/formula/ApplyValuesDateScope.vue";
import ApplyYearScope from "@/components/customAttributes/formula/ApplyYearScope.vue";
import IconCog from "@/components/icons/IconCog.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type {
  IAttribute,
  IContextSetValueModel,
  IStepAttribute,
} from "@/models/orchestration";

const emit = defineEmits<{
  apply: [value: IContextSetValueModel];
}>();

const props = withDefaults(
  defineProps<{
    item: IStepAttribute;
    attribute: IAttribute | null;
    isActive: boolean;
    error?: boolean;
  }>(),
  {
    attribute: null,
    isActive: false,
  }
);

interface ContextConfig {
  component:
    | typeof ApplyScoreModel
    | typeof ApplyDateScope
    | typeof ApplyValuesDateScope
    | typeof ApplyYearScope;
  props: Record<string, unknown>;
  iconClass: unknown;
  dataCy: string;
}

interface ComponentProps {
  item: IStepAttribute;
  attribute: IAttribute | null;
  isActive: boolean;
  error?: boolean;
}

const CONTEXT_FACTORIES: Record<
  string,
  (props: ComponentProps) => ContextConfig
> = {
  scoreModel: (props) => ({
    component: ApplyScoreModel,
    props: { context: props.item.context, attributeId: props.item.id },
    iconClass: { "text-warning": !props.item.context },
    dataCy: "set-score-model-context",
  }),

  dateScope: (props) => ({
    component: ApplyDateScope,
    props: { context: props.item.context },
    iconClass: [
      { "text-warning": !props.item.context },
      props.isActive ? "bg-blue-100" : "bg-gray-100",
      { "bg-red-100": props.error },
    ],
    dataCy: "set-look-back-context",
  }),

  valuesDateScope: (props) => ({
    component: ApplyValuesDateScope,
    props: { context: props.item.context },
    iconClass: { "text-warning": !props.item.context },
    dataCy: "set-look-back-matching-context",
  }),

  yearScope: (props) => ({
    component: ApplyYearScope,
    props: { context: props.item.context },
    iconClass: { "text-warning": !props.item.context },
    dataCy: "set-year-context",
  }),
};

const CONTEXT_TYPE_CHECKS: {
  type: string;
  check: (attr: IAttribute | null) => boolean;
}[] = [
  {
    type: "scoreModel",
    check: (attr: IAttribute | null) =>
      !!attr?.options?.consumer_score_models_levels,
  },
  {
    type: "dateScope",
    check: (attr: IAttribute | null) => !!attr?.options?.month_picker_range,
  },
  {
    type: "valuesDateScope",
    check: (attr: IAttribute | null) => !!attr?.options?.in_array_values,
  },
  {
    type: "yearScope",
    check: (attr: IAttribute | null) =>
      !!Object.prototype.hasOwnProperty.call(attr?.meta, "specific_year"),
  },
];

const getContextType = (attribute: IAttribute | null): string | null => {
  return (
    CONTEXT_TYPE_CHECKS.find(({ check }) => check(attribute))?.type ?? null
  );
};

const contextComponent = computed(() => {
  const contextType = getContextType(props.attribute);
  return contextType ? CONTEXT_FACTORIES[contextType](props) : null;
});

const applyContext = (context: IContextSetValueModel) => {
  emit("apply", context);
};
</script>
